import { apiClient, ApiError } from './apiClient'
import { sessionLockService } from './sessionLockService'

const TIMEZONES = [
  { value: 'Africa/Abidjan', label: 'Abidjan (GMT+0)' },
  { value: 'Africa/Lagos', label: 'Lagos (GMT+1)' },
  { value: 'Europe/Paris', label: 'Paris (GMT+1 / +2)' }
]

const LOCALES = [
  { value: 'fr-FR', label: 'Français (France)' },
  { value: 'fr-CI', label: "Français (Côte d'Ivoire)" },
  { value: 'en-US', label: 'English (US)' }
]

const DATE_FORMATS = [
  { value: 'dd/MM/yyyy', label: '31/12/2026' },
  { value: 'dd-MM-yyyy', label: '31-12-2026' },
  { value: 'yyyy-MM-dd', label: '2026-12-31' }
]

const DEVISES = [
  { value: 'XOF', label: 'Franc CFA (XOF)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'USD', label: 'Dollar US (USD)' }
]

const FNE_ENVIRONNEMENTS = [
  { value: 'sandbox', label: 'Sandbox (test)' },
  { value: 'production', label: 'Production' }
]

let cachedSettings = null
let logoObjectUrl = null
let signatureObjectUrl = null

const normalizeSettings = (settings) => ({
  ...settings,
  id: settings.id,
  defaultCompanyId: settings.defaultCompanyId != null ? String(settings.defaultCompanyId) : '',
  tauxTVADefaut: settings.tauxTVADefaut != null ? Number(settings.tauxTVADefaut) : 18,
  soldeInitial: settings.soldeInitial != null ? Number(settings.soldeInitial) : 0,
  validiteDevisJours: settings.validiteDevisJours != null ? Number(settings.validiteDevisJours) : 30,
  sessionTimeoutMinutes: settings.sessionTimeoutMinutes != null ? Number(settings.sessionTimeoutMinutes) : 60,
  passwordMinLength: settings.passwordMinLength != null ? Number(settings.passwordMinLength) : 8,
  maxLoginAttempts: settings.maxLoginAttempts != null ? Number(settings.maxLoginAttempts) : 5,
  devisPalette: (settings.devisPalette || 'bleu_pro').toLowerCase(),
  devisEnteteContenu: settings.devisEnteteContenu || '',
  devisPiedPageContenu: settings.devisPiedPageContenu || '',
  devisLogoPresent: Boolean(settings.devisLogoPresent),
  devisSignaturePresent: Boolean(settings.devisSignaturePresent),
  devisCouleurEntete: settings.devisCouleurEntete || '',
  devisCouleurPied: settings.devisCouleurPied || '',
  devisCouleurTexteEntete: settings.devisCouleurTexteEntete || '',
  devisCouleurTextePied: settings.devisCouleurTextePied || '',
  paieIndemniteTransportDefaut: settings.paieIndemniteTransportDefaut != null ? Number(settings.paieIndemniteTransportDefaut) : 25000,
  paieTauxLogementDirection: settings.paieTauxLogementDirection != null ? Number(settings.paieTauxLogementDirection) : 10,
  paieTauxLogementStandard: settings.paieTauxLogementStandard != null ? Number(settings.paieTauxLogementStandard) : 5,
  paieTauxPrimeCommercial: settings.paieTauxPrimeCommercial != null ? Number(settings.paieTauxPrimeCommercial) : 5,
  paieTauxCnps: settings.paieTauxCnps != null ? Number(settings.paieTauxCnps) : 6.3,
  paieTauxAmu: settings.paieTauxAmu != null ? Number(settings.paieTauxAmu) : 1,
  paieTauxImpot: settings.paieTauxImpot != null ? Number(settings.paieTauxImpot) : 1.5,
  dashboardSeuilSolde: settings.dashboardSeuilSolde != null ? Number(settings.dashboardSeuilSolde) : null,
  dashboardSeuilBeneficeNet: settings.dashboardSeuilBeneficeNet != null ? Number(settings.dashboardSeuilBeneficeNet) : null,
  dashboardAlertesActives: settings.dashboardAlertesActives !== false,
  updatedAt: settings.updatedAt ? String(settings.updatedAt).split('T')[0] : settings.updatedAt
})

const buildPayload = (data) => ({
  appName: data.appName?.trim(),
  timezone: data.timezone,
  locale: data.locale,
  dateFormat: data.dateFormat,
  defaultCompanyId: data.defaultCompanyId ? Number(data.defaultCompanyId) : null,
  devise: data.devise,
  deviseLabel: data.deviseLabel?.trim(),
  tauxTVADefaut: data.tauxTVADefaut,
  soldeInitial: data.soldeInitial != null ? Number(data.soldeInitial) : 0,
  validiteDevisJours: data.validiteDevisJours,
  conditionsPaiementDefaut: data.conditionsPaiementDefaut?.trim() || '',
  mentionsLegalesDefaut: data.mentionsLegalesDefaut?.trim() || '',
  prefixDevis: data.prefixDevis?.trim().toUpperCase(),
  prefixFacture: data.prefixFacture?.trim().toUpperCase(),
  prefixProjet: data.prefixProjet?.trim().toUpperCase(),
  prefixPaiement: data.prefixPaiement?.trim().toUpperCase(),
  sessionTimeoutMinutes: data.sessionTimeoutMinutes,
  passwordMinLength: data.passwordMinLength,
  maxLoginAttempts: data.maxLoginAttempts,
  notificationsEmail: data.notificationsEmail,
  notificationsRappelDevis: data.notificationsRappelDevis,
  fneActif: data.fneActif,
  fneEnvironnement: data.fneEnvironnement,
  devisEnteteContenu: data.devisEnteteContenu?.trim() || '',
  devisPiedPageContenu: data.devisPiedPageContenu?.trim() || '',
  devisPalette: data.devisPalette || 'bleu_pro',
  devisCouleurEntete: data.devisCouleurEntete || '',
  devisCouleurPied: data.devisCouleurPied || '',
  devisCouleurTexteEntete: data.devisCouleurTexteEntete || '',
  devisCouleurTextePied: data.devisCouleurTextePied || '',
  paieIndemniteTransportDefaut: data.paieIndemniteTransportDefaut != null ? Number(data.paieIndemniteTransportDefaut) : 25000,
  paieTauxLogementDirection: data.paieTauxLogementDirection,
  paieTauxLogementStandard: data.paieTauxLogementStandard,
  paieTauxPrimeCommercial: data.paieTauxPrimeCommercial,
  paieTauxCnps: data.paieTauxCnps,
  paieTauxAmu: data.paieTauxAmu,
  paieTauxImpot: data.paieTauxImpot,
  dashboardSeuilSolde: data.dashboardSeuilSolde != null && data.dashboardSeuilSolde !== '' ? Number(data.dashboardSeuilSolde) : null,
  dashboardSeuilBeneficeNet: data.dashboardSeuilBeneficeNet != null && data.dashboardSeuilBeneficeNet !== '' ? Number(data.dashboardSeuilBeneficeNet) : null,
  dashboardAlertesActives: data.dashboardAlertesActives !== false
})

const revokeLogoUrl = () => {
  if (logoObjectUrl) {
    URL.revokeObjectURL(logoObjectUrl)
    logoObjectUrl = null
  }
}

const revokeSignatureUrl = () => {
  if (signatureObjectUrl) {
    URL.revokeObjectURL(signatureObjectUrl)
    signatureObjectUrl = null
  }
}

export const generalSettingsService = {
  async fetchAll() {
    const data = await apiClient.get('/settings/general')
    cachedSettings = normalizeSettings(data)
    sessionLockService.configure(cachedSettings)
    return cachedSettings
  },

  getCached() {
    return cachedSettings ? { ...cachedSettings } : null
  },

  async update(data) {
    const updated = await apiClient.put('/settings/general', buildPayload(data))
    cachedSettings = normalizeSettings(updated)
    sessionLockService.configure(cachedSettings)
    return cachedSettings
  },

  async uploadDevisLogo(file) {
    const updated = await apiClient.upload('/settings/general/logo', file)
    cachedSettings = normalizeSettings(updated)
    revokeLogoUrl()
    return cachedSettings
  },

  async deleteDevisLogo() {
    const updated = await apiClient.delete('/settings/general/logo')
    cachedSettings = normalizeSettings(updated)
    revokeLogoUrl()
    return cachedSettings
  },

  async getDevisLogoUrl(forceReload = false) {
    if (!cachedSettings?.devisLogoPresent) {
      revokeLogoUrl()
      return null
    }
    if (logoObjectUrl && !forceReload) {
      return logoObjectUrl
    }
    revokeLogoUrl()
    logoObjectUrl = await apiClient.fetchBlob('/settings/general/logo')
    return logoObjectUrl
  },

  async uploadDevisSignature(file) {
    const updated = await apiClient.upload('/settings/general/signature', file)
    cachedSettings = normalizeSettings(updated)
    revokeSignatureUrl()
    return cachedSettings
  },

  async deleteDevisSignature() {
    const updated = await apiClient.delete('/settings/general/signature')
    cachedSettings = normalizeSettings(updated)
    revokeSignatureUrl()
    return cachedSettings
  },

  async getDevisSignatureUrl(forceReload = false) {
    if (!cachedSettings?.devisSignaturePresent) {
      revokeSignatureUrl()
      return null
    }
    if (signatureObjectUrl && !forceReload) {
      return signatureObjectUrl
    }
    revokeSignatureUrl()
    signatureObjectUrl = await apiClient.fetchBlob('/settings/general/signature')
    return signatureObjectUrl
  },

  get() {
    if (cachedSettings) {
      return { ...cachedSettings }
    }
    throw new ApiError('Paramètres non chargés. Appelez fetchAll() d’abord.', 500)
  },

  getTimezones() {
    return TIMEZONES
  },

  getLocales() {
    return LOCALES
  },

  getDateFormats() {
    return DATE_FORMATS
  },

  getDevises() {
    return DEVISES
  },

  getFneEnvironnements() {
    return FNE_ENVIRONNEMENTS
  }
}
